import _ from "lodash";
import { flow, castArray, map } from "lodash/fp";
import { useEffect, useCallback, useState, useMemo } from "react";
import { IFilter, IUseOmniFilterPanel } from "./interfaces";
import { OmniFilterDropdownItemProps } from "./types";
import { DropdownItemProps } from "semantic-ui-react";
import { MainAdvancedFilters } from "../../utils/variables";
import moment from "moment";
import { PermitsCustomFieldTypesNames } from "../../constants/PermitsCustomFieldTypesNames";

function isNumeric(value: string) {
  return /^-?\d+$/.test(value);
}

type IBalanceDueFilter = "empty" | "not_empty" | { greater_than: string } | { less_than: string } | string;

export const filterStateToGQL = (filter: any, size: number, refreshCursor: any, sort: any): any => {
  delete filter.cityId;

  const tempSort: any = [{}];
  if (sort) {
    const sortKey = Object.keys(sort[0])[0];
    tempSort[0][sortKey] = sort[0][sortKey].order;
  }

  // full_text parameter for gql query
  const full_text = {
    keyword: filter.q || "",
    fields: filter.q_fields || {},
  };

  const tempFilter = {
    permits_type_id: [],
    status: [],
    assignee_ids: [],
    ...filter,
  };
  const filtersExpression = filter.filtersExpression ? filter.filtersExpression[0] : "";
  if (tempFilter.filtersExpression) {
    delete tempFilter.filtersExpression;
  }

  // if filtering by permit types, rename to 'permits_type_id' (for backend call)
  if (tempFilter.types !== undefined) {
    tempFilter.permits_type_id = tempFilter?.types;
    delete tempFilter.types;
  }
  delete tempFilter.q;
  delete tempFilter.q_fields;
  delete tempFilter.filtersExpression;
  const mainFilterFields = ["permits_type_id", "status", "assignee_ids"];
  Object.keys(tempFilter).map((key: string) => {
    // sets advanced filters
    if (
      key.includes("custom_field") ||
      key.includes("contact") ||
      key.includes("primary_location") ||
      MainAdvancedFilters.map((filter: any) => filter.typesId).includes(key)
    ) {
      const customFilter = JSON.parse(tempFilter[key] || "{}");

      let value: any = {};
      if (["empty", "not_empty", "checked", "not_checked"].includes(customFilter.operator)) {
        value = customFilter.operator;
      } else if (
        ["less_than", "greater_than", "not_equal", "starts_with", "contains", "not_contain", "prefix"].includes(
          customFilter.operator,
        )
      ) {
        value[customFilter.operator] = customFilter.value;
      } else if (
        ["is_before", "is_after"].includes(customFilter.operator) &&
        (PermitsCustomFieldTypesNames.DATE_PICKER || customFilter.locationFieldType === "value_datetime")
      ) {
        value[customFilter.operator] = moment(customFilter.value).format("YYYY-MM-DD");
      } else if (["is_between"].includes(customFilter.operator)) {
        if (
          customFilter.type == PermitsCustomFieldTypesNames.DATE_PICKER ||
          customFilter.locationFieldType === "value_datetime"
        ) {
          value[customFilter.operator] = [moment(customFilter.value.from), moment(customFilter.value.to)];
        } else if (
          customFilter.type == PermitsCustomFieldTypesNames.WHOLE_NUMBER ||
          customFilter.locationFieldType === "value_number"
        ) {
          value[customFilter.operator] = [customFilter.min, customFilter.max];
        }
      } else if (["is_any", "not_any", "equals"].includes(customFilter.operator)) {
        value[customFilter.operator] = customFilter.value;
      }
      tempFilter[key] = value;
    }

    // sets all other filters
    else {
      let val = tempFilter[key];
      if (Array.isArray(val)) {
        val = val.map((v: any) => (isNumeric(v) ? parseInt(v) : v)); // turn numeric strings into numbers
      }
      // convert date ranges into proper format
      if (val?.from || val?.to) {
        tempFilter[key] = {
          is_between: [moment(val.from), moment(val.to)],
        };
      } else {
        tempFilter[key] = { is_any: val };
      }
    }
  });

  const searchFilters: any = {};
  Object.keys(tempFilter).forEach((filter: any, index: number) => {
    if (tempFilter[filter]) {
      const value: any = Object.values(tempFilter[filter])[0];
      const temp: any = {};
      let tempKey = filter;
      if (filter === "applicant_name") {
        tempKey = "creator.full_name";
      } else if (filter === "balance_due") {
        tempKey = "total_pending";
      } else if (filter === "application_id") {
        tempKey = "id";
      } else if (filter === "applicant_email") {
        tempKey = "creator.email";
      } else if (filter === "formal_number") {
        tempKey = "reservation_number";
      } else if (filter === "permit_number") {
        tempKey = "reservation_number";
      }
      if (Array.isArray(value) && !mainFilterFields.includes(filter)) {
        temp[tempKey] = tempFilter[filter];
        if (tempKey === "total_pending" || tempKey === "subtotal") {
          temp[tempKey] = tempFilter[filter];
          const key: string = Object.keys(tempFilter[filter])[0];
          if (key === "is_between") {
            temp[tempKey][key][0] = parseInt(temp[tempKey][key][0]) * 100;
            temp[tempKey][key][1] = parseInt(temp[tempKey][key][1]) * 100;
          }
        } else {
          temp[tempKey] = tempFilter[filter];
        }
      } else {
        if (tempKey === "total_pending" || tempKey === "subtotal") {
          temp[tempKey] = tempFilter[filter];
          const key: string = Object.keys(tempFilter[filter])[0];

          const value: IBalanceDueFilter = tempFilter[filter];
          const isEquals = _.isString(value) && value !== "empty" && value !== "not_empty";

          if (key === "greater_than" || key === "less_than") {
            temp[tempKey][key] = Math.floor(parseFloat(temp[tempKey][key]) * 100);
          } else if (isEquals) {
            temp[tempKey] = Math.floor(parseFloat(tempFilter[filter]) * 100);
          }
        } else {
          temp[tempKey] = tempFilter[filter];
        }
      }
      searchFilters[index + 1] = temp;
    }
  });

  return {
    size: size,
    from: refreshCursor,
    sort: tempSort,
    full_text,
    filters_expression: filtersExpression,
    filters: searchFilters,
  };
};

export const useOmniFilter = ({ ref, filters, initialFilterState }: IUseOmniFilterPanel) => {
  const [options, setOptions] = useState<OmniFilterDropdownItemProps[]>([]);
  const [filterState, setFilterState] = useState<{ [key: string]: string[] }>(initialFilterState || {});
  const [query, setQuery] = useState({});
  const [dirty, setDirty] = useState(false);
  const processedFilters: { [key: string]: IFilter } = useMemo(() => {
    return _.reduce(
      filters,
      (result: any, filter: any, key: string) => {
        switch (filter.render) {
          case "boolean":
            filter.max = 1;
            filter.options = [
              {
                key: true,
                value: true,
                text: "Yes",
              },
              {
                key: false,
                value: false,
                text: "No",
              },
            ];
            break;
          case "freeform":
            filter.max = 1;
            filter.options = [
              {
                key: true,
                value: "",
                text: "...",
              },
            ];
            break;
        }
        result[key] = filter;
        return result;
      },
      {},
    );
  }, [filters]);
  const _setFilterState = useCallback((state: any) => {
    const newState = _.reduce(
      state,
      (result: any, values: any, key: string) => {
        if (processedFilters[key]) {
          const filter = processedFilters[key];
          const compacted = _.filter(
            _.castArray(values),
            (v: any) => _.isBoolean(v) || (_.isString(v) && !_.isEmpty(v)),
          );
          if (!_.isEmpty(compacted) || filter.render) {
            result[key] = compacted;
          }
        }
        if (["search", "status", "assignee_ids", "q_fields", "types", "q", "filtersExpression"].includes(key)) {
          result[key] = state[key];
        }
        if (
          key.includes("custom_field") ||
          key.includes("contact") ||
          key.includes("primary_location") ||
          key.includes("active_workflow_steps.title") ||
          MainAdvancedFilters.map((filter: any) => filter.typesId).includes(key)
        ) {
          result[key] = state[key];
        }
        return result;
      },
      {},
    );
    setFilterState(newState);
  }, []);
  const valueToOption = useCallback(
    (value: string) => options.find((option: any) => option.value === value),
    [options],
  );
  const textToOption = useCallback((text: string) => options.find((option: any) => option.text === text), [options]);
  const removeValue = useCallback(
    (filterKey: string, removeValue: string) => {
      const [, removeFilterValue] = removeValue.split(":");
      const values = _.compact(filterState[filterKey].filter((value: string) => value !== removeFilterValue));
      const newState = _.pickBy(
        Object.assign({}, filterState, { [filterKey]: _.isEmpty(values) ? undefined : values }),
        _.identity,
      );
      setFilterState(newState);
      setDirty(true);
    },
    [filterState, setDirty, setFilterState],
  );
  const loading = useMemo(() => {
    return _.some(_.map(processedFilters, ({ query }: any) => query?.isLoading));
  }, [processedFilters]);
  const filteredOptions = useMemo(() => {
    const selectedOptions = _.flatMap(filterState, (values: string[], key: string) => {
      return _.map(values, (value: string) => `${key}:${value}`);
    });
    return options.filter((option: DropdownItemProps) => !selectedOptions.includes(option.key));
  }, [options, filterState]);
  const header = useMemo(() => {
    const tempFilters = { ...processedFilters };
    delete tempFilters.types;
    delete tempFilters.application_id;
    delete tempFilters.primary_identifier;
    delete tempFilters.permit_number;
    delete tempFilters.applicant;
    delete tempFilters.has_balance;
    delete tempFilters.submitted_at;
    delete tempFilters.expired_at;
    return [
      "Search by ",
      _.take(_.shuffle(_.compact(_.map(tempFilters, ({ label }: { label: string }) => label))), 2).join(", "),
      ", and more...",
    ].join("");
  }, [processedFilters]);
  const props = useMemo(
    () => ({
      ref,
      header,
      loading,
      options: filteredOptions,
      removeValue,
      setFilterState: _setFilterState,
      filterState,
      dirty,
      setDirty,
      valueToOption,
      textToOption,
      filters: processedFilters,
    }),
    [
      ref,
      header,
      loading,
      filteredOptions,
      removeValue,
      processedFilters,
      _setFilterState,
      filterState,
      setDirty,
      dirty,
      valueToOption,
      textToOption,
    ],
  );

  useEffect(() => {
    const calculateQuery = async () => {
      const query: { [key: string]: any } = {};
      for (const key of _.keys(filterState)) {
        const filter = processedFilters[key];
        const values = flow(
          castArray,
          map((value: string) => {
            if (filter?.render === "boolean") {
              if (_.isBoolean(value)) {
                return value;
              }
              return value === "true" ? true : value === "false" ? false : undefined;
            }
            return value;
          }),
        )(filterState[key]);
        if (filter?.resolver) {
          let value: string | boolean | undefined;
          for (value of values) {
            const resolved = await filter?.resolver(filterState, value);
            Object.assign(query, { [key]: _.uniq((query[key] || []).concat(resolved[0])) }, resolved[1]);
          }
        } else {
          Object.assign(query, { [key]: _.uniq((query[key] || []).concat(values)) });
        }
      }
      setQuery(
        _.reduce(
          query,
          (result: any, value: any, key: any) => {
            if (processedFilters[key]?.transient) {
              return result;
            }
            if (processedFilters[key]?.array && !_.isEmpty(_.compact(_.castArray(value)))) {
              result[key] = _.uniq(_.compact(_.castArray(value)));
            } else if (processedFilters[key]?.collapse) {
              result[key] = processedFilters[key]?.collapse(value);
            } else if (processedFilters[key]?.array === false) {
              result[key] = value[0];
            } else {
              if ((_.isArray(value) && !_.isEmpty(_.compact(value))) || !_.isArray(value)) {
                result[key] = value;
              }
            }
            return result;
          },
          {},
        ),
      );
    };
    if (!loading) {
      calculateQuery();
    }
  }, [loading, processedFilters, filterState]);

  useEffect(() => {
    const tempFilters = { ...processedFilters };
    setOptions(
      _.flatMap(tempFilters, (filter: IFilter, filterKey: string) => {
        const data = _.get(filter.query, filter.dataPath || "data.payload.data", []);
        return (filter.options || [])
          .map((option: DropdownItemProps) => ({
            key: `${filterKey}:${option.key}`,
            value: `${filterKey}:${option.value}`,
            text: `${filter.label}: ${option.text}`,
            filter: filter,
          }))
          .concat(
            data.map((data: any) => ({
              key: `${filterKey}:${data.id}`,
              value: `${filterKey}:${data.id}`,
              text: `${filter.label}: ${(filter as any)?.toText(data)}`,
              filter: filter,
            })),
          );
      }),
    );
  }, [processedFilters]);

  return {
    query,
    filterState,
    setFilterState: _setFilterState,
    dirty,
    setDirty,
    props,
  };
};
